/**
 * Universal field-side hookup for input/cell components.
 *
 * Wraps the parse/format/emit dance every component does the same way:
 * read raw input → run it through the FieldType chain's `parse` → emit
 * the normalized value on `update:modelValue`. `displayed` is the inverse
 * — runs the stored value through `format` for display.
 *
 * Used by every input regardless of which native element it renders
 * (`<input>`, `<select>`, `<textarea>`, checkbox, modal button). Cells
 * and filter pickers added later will use it the same way.
 */

import { type Field, formatValue, parseValue } from "@auger/core/fields"

/** Cell lifecycle a field renderer draws for: in-row display, lifted-but-read-only
 *  (active), or the live editor. GridCell derives it from its active/editing flags. */
export type FieldMode = "inline" | "active" | "editing"

/** Component size scale, shared across field renderers and inputs. */
export type FieldSize = "small" | "normal" | "medium" | "large"

/** Size → text-size utility class. */
export function textSizeClass(size: FieldSize | undefined): string {
  const sizeMap: Record<FieldSize, string> = {
    small: "text-7",
    normal: "text-6",
    medium: "text-5",
    large: "text-4",
  }
  return sizeMap[size ?? "normal"] || "text-6"
}

type EmitFn = (event: "update:modelValue", value: unknown) => void

export function useField(field: Field, emit: EmitFn) {
  const handleInput = (rawOrEvent: unknown): void => {
    const raw =
      rawOrEvent instanceof Event
        ? (rawOrEvent.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value
        : rawOrEvent
    emit("update:modelValue", parseValue(field, raw))
  }

  const displayed = (value: unknown): unknown => formatValue(field, value)

  return { handleInput, displayed }
}
